import { Directive, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core'
import { Subscription, timer } from 'rxjs'
import { finalize, switchMap } from 'rxjs/operators'
import { ApiService } from 'src/app/services/api.service'
import { RecoveryPointDto } from 'src/app/services/dtos'
import { UiService } from 'src/app/services/ui.service'
import { RpSliderChangedEvent } from 'src/app/shared/rp-range-slider/rp-range-slider.component'
import { shortTimeString } from 'src/app/shared/utils'
import { subscriptionActive } from 'src/utils/rxjs-utils'
import { uniquify } from 'src/utils/uniquify'
import { AccountService } from 'src/app/account/services/account.service'
import { Application, ApplicationProviderService } from 'src/app/account/services/application.service'
import { AccessLevelService, AppPermission } from 'src/app/services/access-level.service'

export interface LaunchRestoreSettings {
  application: Application
  recoveryPointId?: string
  isRealTime?: boolean
  networkSandboxEnabled?: boolean
  networkSandboxWhitelist?: string[]
  networkSandboxAllowedDomains?: string[]
  networkSandboxAllowedServices?: string[]
  quarantineEnabled?: boolean
  advancedNetworkSandbox?: boolean
  forceNewTargetPlan?: boolean
  protectRecoveryPoints?: boolean
}

@Directive()
export abstract class LaunchSyncDialogBase implements OnInit, OnDestroy {
  constructor(
    private applicationProviderService: ApplicationProviderService,
    protected accountService: AccountService,
    protected apiService: ApiService,
    protected uiService: UiService,
    protected accessLevelService: AccessLevelService
  ) {}

  private applicationSubscription: Subscription = null
  protected _applications: Array<Application> = null
  private _refreshSubscriptions: Map<string, Subscription> = null
  protected selectedApplicationIds: Set<string> = null
  private applicationRecoveryPointsSubscription: Subscription = null
  private selectedRecoveryPoint: RecoveryPointDto = null
  protected isRealTime: boolean = false
  protected onlyProtectedRps: boolean = false
  private canAdjustRecoveryPointSticky: boolean = null
  private appPermissionsMapCache: Map<string, Set<string>> | null = null
  private isLegacyAdminCache: boolean = false

  @Input() application: Application
  @Input() launching: boolean = false
  @Output() cancel = new EventEmitter<null>()

  // Used for unit tests.
  setApplication(app: Application) {
    this.application = app
  }

  areAllApplicationsSelected(): boolean {
    return this.selectedApplications.length === this.applications.length && this.applications.length > 0
  }

  toggleSelectAllApplications(checked: boolean): void {
    if (checked) {
      this.selectedApplicationIds = new Set(this.applications.map((app) => app.applicationObj.applicationId))
    } else {
      this.selectedApplicationIds = new Set()
    }
  }

  ngOnInit() {
    // initialize the selected recovery point.  This will get overridden immediately by the recovery point slider,
    // but in some cases (i.e. locked recovery point), the slider doesn't show and this doesn't get
    // updated there.
    // Pick the latest recovery point.
    this.selectedRecoveryPoint = this.application.recoveryPoints.reduce(
      (accum, curr) => (!!accum ? (accum.timestamp > curr.timestamp ? accum : curr) : curr),
      null
    )
    this.isRealTime = this.application.rpo === 0

    // build the list of applications that should display in the UI
    this.selectedApplicationIds = new Set<string>()
    this.selectedApplicationIds.add(this.application.applicationId)

    let that = this
    function isSyncableApplication(application: Application) {
      // Only the regions need to match for multi-account
      return (
        application.sourceRegion === that.application.sourceRegion &&
        application.targetRegion === that.application.targetRegion
      )
    }

    // Get the currently-loaded applications from the application provider service and filter by region
    this._applications = this.applicationProviderService
      .loadedApplications()
      .filter((app) => isSyncableApplication(app))

    // Load and cache user permissions for filtering applications
    this.accessLevelService.getUserPermissions().subscribe((permissions) => {
      this.appPermissionsMapCache = permissions.appPermissionsMap || new Map()
      this.isLegacyAdminCache = permissions.isLegacyAdmin
    })

    // Always refresh recovery points when dialog opens to ensure we have the latest data.
    // This prevents issues where stale cached RPs are displayed if the user has had the app
    // open for a while without refreshing. Using stale RPs can cause test/failover failures
    // if an RP was deleted between when it was cached and when the test is triggered.
    this._refreshSubscriptions = new Map<string, Subscription>(
      this._applications.map((app) => {
        // Use timer() to launch this asynchronously.  Otherwise, it can complete
        // before the subscription is added to the map and we get an error when
        // we try to delete it.
        let refreshObservable = timer(0).pipe(
          switchMap(() => app.refreshRecoveryPoints$()),
          finalize(() => this._refreshSubscriptions.delete(app.applicationId))
        )
        return [app.applicationId, refreshObservable.subscribe()]
      })
    )
  }

  ngOnDestroy() {
    if (subscriptionActive(this.applicationSubscription)) {
      this.applicationSubscription.unsubscribe()
    }
    this.applicationSubscription = null

    if (subscriptionActive(this.applicationRecoveryPointsSubscription)) {
      this.applicationRecoveryPointsSubscription.unsubscribe()
    }
    this.applicationRecoveryPointsSubscription = null
  }

  get applications() {
    return this._applications
      .filter((app) => this.applicationIsCandidate(app) && this.hasRequiredAccessForOperation(app.applicationId))
      .map((application) => {
        return {
          applicationObj: application,
          selected: this.selectedApplicationIds.has(application.applicationId),
        }
      })
      .sort((applicationLeft, applicationRight) => {
        if (applicationLeft.applicationObj.applicationId === this.application.applicationId) {
          return -1
        }
        if (applicationRight.applicationObj.applicationId === this.application.applicationId) {
          return 1
        }
        if (applicationLeft.applicationObj.name < applicationRight.applicationObj.name) {
          return -1
        }
        return 1
      })
  }

  applicationTrackBy(index: number, item: { applicationObj: Application; selected: boolean }) {
    return item.applicationObj.applicationId
  }

  get selectedApplications() {
    return this._applications.filter((application) => this.selectedApplicationIds.has(application.applicationId))
  }

  protected abstract applicationIsCandidate(application: Application)

  /**
   * Get the required permission for this operation
   * Subclasses must override this to specify which permission is needed
   */
  protected abstract getRequiredPermission(): AppPermission

  /**
   * Check if user has the required permission for the operation on a specific application
   */
  protected hasRequiredAccessForOperation(applicationId: string): boolean {
    // If RBAC is not enabled, allow all operations
    if (!this.accountService.rbacEnabled) {
      return true
    }

    // If permissions haven't loaded yet, allow the application (permissions will be checked on actual operation)
    if (!this.appPermissionsMapCache || this.isLegacyAdminCache) {
      return true
    }

    const userPermissions = this.appPermissionsMapCache.get(applicationId)

    // If user has no permissions for this app, deny
    if (!userPermissions || userPermissions.size === 0) {
      return false
    }

    // Check if user has the required permission
    return userPermissions.has(this.getRequiredPermission())
  }

  applicationIsChecked(application: Application) {
    return this.selectedApplicationIds.has(application.applicationId)
  }

  onCancelClick() {
    this.cancel.emit(null)
  }

  toggleApplicationSelection(applicationId: string) {
    if (this.selectedApplicationIds.has(applicationId)) {
      this.selectedApplicationIds.delete(applicationId)
    } else {
      this.selectedApplicationIds.add(applicationId)
    }
  }

  get canAdjustRecoveryPoint() {
    return true
  }

  get loading() {
    // if we're querying recovery points or loading permissions, we're still loading the details for this page.
    return subscriptionActive(this.applicationRecoveryPointsSubscription) || this.appPermissionsMapCache === null
  }

  get recoveryPoints() {
    // We need to aggregate these across all applications and uniquify them
    let allRecoveryPoints = this._applications.reduce((accum: Array<RecoveryPointDto>, application: Application) => {
      if (this.selectedApplicationIds.has(application.applicationId)) {
        let app_rps = application.recoveryPoints || []
        if (this.onlyProtectedRps) {
          app_rps = app_rps.filter((rp) => rp.protected)
        }
        accum = accum.concat(app_rps)
      }
      return accum
    }, [])
    // If multiple applications share a given recovery point, it's in the list multiple times.  Let's uniquify it.
    allRecoveryPoints = uniquify(allRecoveryPoints, (rpDto) => rpDto.recoveryPointId)

    if (!allRecoveryPoints.some((rp) => rp.protected)) {
      // If we don't have any retained recovery points, make sure the checkbox is unchecked
      this.onlyProtectedRps = false
    }

    return allRecoveryPoints
  }

  computeApplicationsRecoveryPoint() {
    for (const app of this._applications) {
      this.computeApplicationRecoveryPoint(app)
    }
  }

  computeApplicationRecoveryPoint(application: Application) {
    // For this application, find the newest recovery point that is older or equal to the selected recovery point
    let rps = []
    if (!this._refreshSubscriptions.has(application.applicationId)) {
      rps = application.recoveryPoints || []
    }
    if (this.onlyProtectedRps) {
      rps = rps.filter((rp) => rp.protected)
    }
    // sort them in decreasing order
    rps = rps.sort((l, r) => r.timestamp.valueOf() - l.timestamp.valueOf())
    // filter to the recovery points that are not newer than the selected recovery point
    if (this.selectedRecoveryPoint) {
      rps = rps.filter((rpDto) => rpDto.timestamp <= this.selectedRecoveryPoint.timestamp)
    }
    if (rps.length > 0) {
      // return the newest of these (first in the reverse-sorted list)
      return rps[0]
    }
    // none work, so return null
    return null
  }

  get recoveryPointSnapshotTime() {
    return this.selectedRecoveryPoint ? this.rpTimeString(this.selectedRecoveryPoint) : ''
  }

  showLock(appRp: RecoveryPointDto) {
    return this.protectRecoveryPointEnabled && appRp.protected
  }

  recoversToTimeString(application: Application, appRp: RecoveryPointDto) {
    if (this.isRealTime) {
      if (application.rpo === 0) {
        return 'Now'
      }
    }
    return this.rpTimeString(appRp)
  }

  rpTimeString(rp: RecoveryPointDto) {
    return shortTimeString(rp.timestamp)
  }

  get recoveryPointTime() {
    if (this.isRealTime) {
      return 'Now'
    }
    return this.recoveryPointSnapshotTime
  }

  recoveryPointChanged(rpSliderChangeEvent: RpSliderChangedEvent) {
    this.selectedRecoveryPoint = rpSliderChangeEvent.recoveryPointDto
    this.isRealTime = rpSliderChangeEvent.isRealTime
  }

  isRealTimeApplication(appliction: Application) {
    return appliction.rpo === 0
  }

  get showRetainedRpsCheckbox() {
    return this.protectRecoveryPointEnabled && this.recoveryPoints.some((rp) => rp.protected)
  }

  get selectedIsRealTime() {
    return this.isRealTime
  }

  get selectedRecoveryPointId() {
    return !!this.selectedRecoveryPoint ? this.selectedRecoveryPoint.recoveryPointId : null
  }

  get enableRealTimeRecovery() {
    // return true if any applications have RPO == 0
    return this._applications.reduce((prev, app) => prev || app.rpo == 0, false)
  }

  get protectRecoveryPointEnabled(): boolean {
    return this.accountService.protectRecoveryPointEnabled
  }
}
